import React, { useEffect, useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { cn } from '../../../../lib/utils';
import ApiService from '../../../../services/api.service';
import HistoryTable from './components/HistoryTable';

export default function ReviewHistoryPage() {
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [historySearchInput, setHistorySearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [historySort, setHistorySort] = useState('updatedAt');
  const [template, setTemplate] = useState('');
  const [skillStatistic, setSkillStatistic] = useState([]);
  const [localSearchValue, setLocalSearchValue] = useState('');

  const { department } = useParams();

  const fetchReviewHistory = useCallback(
    async (skip, limit, search = '', sort = '') => {
      try {
        const response = await ApiService.head.getReviewHistory(department, {
          params: {
            search,
            skip: skip * limit,
            limit,
            sort,
          },
        });

        const statistic = await Promise.all(
          response.paginatedResult.map(({ userId, _id }) =>
            ApiService.head.getReviewStatistic(department, userId, _id),
          ),
        );

        setSkillStatistic(statistic);
        setCurrentPage(skip);
        setReviewHistoryData(response.paginatedResult);
        setTotalCount(response.totalCount ?? 0);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    },
    [department],
  );

  const fetchTemplate = async () => {
    try {
      const response = await ApiService.admin.getReviewStatisticTemplate();
      setTemplate(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isLoading) {
      fetchTemplate();
      fetchReviewHistory(currentPage, rowsPerPage, historySearchInput, historySort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const debouncedSearch = useDebouncedCallback((value) => {
    setHistorySearchInput(value);
    setCurrentPage(0);
    setIsLoading(true);
  }, 300);

  const handleInputChange = (event) => {
    const { value } = event.target;
    setLocalSearchValue(value);
    debouncedSearch(value.trim());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Review History</h1>
          <p className="text-muted-foreground">
            View and manage past review records for your department
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by email..."
            value={localSearchValue}
            onChange={handleInputChange}
            className={cn(
              'w-64 pl-9 pr-4 py-2 rounded-md text-sm',
              'bg-background border border-input',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
              'placeholder:text-muted-foreground',
            )}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card" style={{ borderColor: 'hsl(var(--border))' }}>
        <HistoryTable
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          reviewHistoryData={reviewHistoryData}
          totalCount={totalCount}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          setSort={setHistorySort}
          fetchData={fetchReviewHistory}
          historySearchInput={historySearchInput}
          historySort={historySort}
          template={template}
          skillStatistic={skillStatistic}
        />
      </div>
    </div>
  );
}
